// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Suspense } from 'react';
import * as ReactDOMClient from 'react-dom/client';

import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { createBrowserHistory } from 'history';

import App from './app/App.lazy';
import { createUpdateEvent } from './common/hooks/useServiceWorker';
import { initializeMsal } from './common/utils/authentication/authenticationProvider';
import i18n from './i18n';
import './index.css';
import { Splash } from './layout/Splash/Splash';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// eslint-disable-next-line unicorn/prefer-query-selector
const root = document.getElementById('root');
const applicationInsightsKey: string = process.env.REACT_APP_APPLICATION_INSIGHTS_KEY as string;
const browserHistory = createBrowserHistory();
export const reactPlugin = new ReactPlugin();

// Set lang attribute for accessibility (WCAG 3.1.1)
const updateHtmlLang = () => {
  document.documentElement.lang = i18n.language;
};

// Set initial language
updateHtmlLang();

// Update lang attribute when language changes
i18n.on('languageChanged', updateHtmlLang);

let appInsights: ApplicationInsights | undefined;
if (applicationInsightsKey) {
  appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: applicationInsightsKey,
      enableAutoRouteTracking: true,
      autoTrackPageVisitTime: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableAjaxErrorStatusText: true,
      enableAjaxPerfTracking: true,
      enableUnhandledPromiseRejectionTracking: false,
      disableExceptionTracking: true,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history: browserHistory },
      },
    },
  });
  if (appInsights) {
    appInsights.loadAppInsights();

    // Manual exception tracking via global error handlers
    // This is a workaround for the _aiHooks bug in Application Insights v3
    const appInsightsInstance = appInsights;
    // eslint-disable-next-line prettier/prettier
    window.addEventListener('error', (event) => {
      appInsightsInstance.trackException({
        exception: event.error || new Error(event.message),
        properties: {
          source: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Track unhandled promise rejections
    // eslint-disable-next-line prettier/prettier
    window.addEventListener('unhandledrejection', (event) => {
      appInsightsInstance.trackException({
        exception: event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        properties: {
          type: 'unhandledrejection',
        },
      });
    });
  }
}

const renderApp = async () => {
  // MSAL v4 requires initialization before any other API calls
  await initializeMsal();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ReactDOMClient.createRoot(root).render(
    <Suspense fallback={<Splash />}>
      <App />
    </Suspense>,
  );
};

renderApp();

serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    if (registration?.waiting) {
      document.dispatchEvent(createUpdateEvent(registration.waiting));
    }
  },
});

const checkForUpdates = () => {
  serviceWorkerRegistration.checkForUpdate();

  // This is necessary because sometimes the app won't recognize it anymore.
  serviceWorkerRegistration.getWaitingServiceWorker().then((worker) => {
    if (worker) {
      document.dispatchEvent(createUpdateEvent(worker));
    }
  });

  setTimeout(checkForUpdates, 60 * 1000 * 5);
};

// App checks every 5 minutes for updates:
checkForUpdates();

window.localStorage.fluentUIDebug = process.env.NODE_ENV !== 'production';
